import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { isValid, parse } from 'date-fns'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/dist/style.css'

import { formatWrapped } from 'services/date'
import { usePopperDialog } from './use-popper-dialog'

import './DayPickerInput.scss'

/**
 * @param {String} str
 * @returns {Boolean}
 */
function isNonEmptyString (str) {
  return typeof str === 'string' && str.length > 0
}

/**
 * Returns string with classnames.
 * @param {String} className
 * @returns {String}
 */
function getClass (className, alert) {
  const cls = ['DayPickerInput']
  if (className) cls.push(className)
  if (alert) cls.push('DayPickerInput--alert')
  return cls.join(' ')
}

const DATE_FORMAT = 'y-MM-dd'

/**
 * @callback OnChangeCb
 * @param {Date} day
 * @return {void}
 */

/**
 * Uncontrolled Day Picker input
 * @param {Object} params
 * @param {String} params.placeholder
 * @param {OnChangeCb} params.onChange
 * @param {value} params.value
 * @param {dateFormat} params.dateFormat
 * @returns {import('react').ReactElement}
 */
export function DayPickerInput ({
  placeholder: placeholderParam,
  onChange,
  value = '',
  dateFormat = DATE_FORMAT,
  disabled,
  required = false,
  className,
  alert,
}) {
  const [selected, setSelected] = useState()
  const [inputValue, setInputValue] = useState(value)

  const {
    isPopperOpen,
    closePopper,
    openPopper,
    popperRef,
    setPopperElement,
    popper,
  } = usePopperDialog()

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const handleInputChange = (e) => {
    setInputValue(e.currentTarget.value)
    const date = parse(e.currentTarget.value, dateFormat, new Date())
    if (isValid(date)) {
      setSelected(date)
    } else {
      setSelected(undefined)
    }
  }

  const handleDaySelect = (date) => {
    setSelected(date)
    if (date) {
      setInputValue(formatWrapped(date, dateFormat))
      if (typeof onChange === 'function') {
        onChange(date)
      }

      closePopper()
    } else {
      setInputValue('')
    }
  }
  const placeholder = isNonEmptyString(placeholderParam)
    ? placeholderParam
    : formatWrapped(new Date(), dateFormat)

  return (
    <div>
      <div
        className={getClass(className, alert)}
        ref={popperRef}
        >
        <input
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onClick={openPopper}
          // removed className="input-reset pa2 ma2 bg-white black ba"
          // is there any reason not to remove these?
          disabled={disabled}
          required={required}
        />
      </div>
      {isPopperOpen && (
        <div
          tabIndex={-1}
          style={popper.styles.popper}
          className="dialog-sheet"
          {...popper.attributes.popper}
          ref={setPopperElement}
          role="dialog"
        >
          <DayPicker
            initialFocus={isPopperOpen}
            mode="single"
            defaultMonth={selected}
            selected={selected}
            onSelect={handleDaySelect}
            className="DayPickerInput-Overlay"
            modifiersClassNames={{
              day: 'DayPicker-Day',
              today: 'DayPicker-Day--today',
              disabled: 'DayPicker--interactionDisabled',
            }}
            components={{
              IconRight: () => (
                <div className="DayPicker-NavButton DayPicker-NavButton--next"></div>
              ),
              IconLeft: () => (
                <div className="DayPicker-NavButton DayPicker-NavButton--prev"></div>
              ),
            }}
          />
        </div>
      )}
    </div>
  )
}

DayPickerInput.protoTypes = {
  placeholder: PropTypes.string,
  alert: PropTypes.bool,
}

export default DayPickerInput
